"use client";

import type { ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Carousel, Section } from "../../primitives";
import { Testimonial, type TestimonialProps } from "../../components";
import { type DrlpCta } from "../cta";
import DrlpSectionHeader from "../section-header";

export interface DrlpTestimonialsProps {
  sectionMetadata: { analyticsId: string };
  title: ReactNode;
  /** Eyebrow label above the heading (e.g. "CUSTOMER TESTIMONIALS"). */
  badge?: string;
  /** Supporting copy. Pass an array to render multiple stacked paragraphs. */
  description?: string | string[];
  /** Optional CTAs under the header — the Figma "w/ CTA" variant. */
  primaryCta?: DrlpCta;
  secondaryCta?: DrlpCta;
  align?: "vertical" | "horizontal";
  /**
   * The testimonials to show. One card renders as a static testimonial; two or
   * three render as a company-tabbed autoplay carousel (Figma 1-up / 2-up / 3-up).
   */
  cards: (Pick<TestimonialProps, "company" | "quote" | "author" | "image"> & {
    cardMetadata: { analyticsId: string };
    thumbnailMetadata: { analyticsId: string };
  })[];
  /**
   * Tailwind background utility for the carousel highlight — the selected-tab
   * progress bar. Both sites render this section under `.brand-i2y4`, so the
   * per-site accent is chosen by the consuming app's DRLP format fn, not the
   * brand cascade: defaults to Fin orange (`bg-fin`); intercom-com overrides to
   * `bg-helpdesk`.
   */
  accentClassName?: string;
  /**
   * Explicit section id. Needed when a v1 and v2 testimonials section appear on
   * the same page for comparison, since the title is usually a ReactNode and
   * can't be slugged into a unique id.
   */
  id?: string;
}

export default function DrlpTestimonials({
  title,
  badge,
  description,
  primaryCta,
  secondaryCta,
  align,
  cards,
  accentClassName = "bg-fin",
  id: idProp,
  sectionMetadata,
}: DrlpTestimonialsProps) {
  const titleText = typeof title === "string" ? title : "testimonials";
  const regionId = badge || titleText;
  const id = idProp ?? regionId.toLowerCase().replaceAll(" ", "-");

  return (
    <Section
      id={id}
      data-analytics-region={regionId}
      data-analytics-id={sectionMetadata.analyticsId}
      data-testid="drlp-testimonials"
      gridCols="10"
      className="py-16 md:py-20"
    >
      <div className="col-span-full grid grid-cols-subgrid gap-y-6 md:gap-y-12">
        <DrlpSectionHeader
          title={title}
          badge={badge}
          description={description}
          primaryCta={primaryCta}
          secondaryCta={secondaryCta}
          align={align}
        />

        <div className="col-span-full">
          {cards.length <= 1 ? (
            <Testimonial
              {...cards[0]}
              // eslint-disable-next-line analytics/id-must-be-static -- each card in the cards prop carries its own cardMetadata.analyticsId, built by the caller from a Sanity array-item key, not copy, a slug, or a translated string
              analyticsId={cards[0].cardMetadata.analyticsId}
              verticalAlignment="center"
              dotDecoratorClassName="text-clay"
              className="bg-chalk border-current/10 w-full border"
            />
          ) : (
            <Carousel.Root autoplayOptions={{ delay: 7500 }}>
              <Carousel.Thumbnails className="flex gap-0">
                {cards.map((card, index) => (
                  <Carousel.Thumbnail
                    key={card.company.name}
                    index={index}
                    // eslint-disable-next-line analytics/id-must-be-static -- each card in the cards prop carries its own thumbnailMetadata.analyticsId, built by the caller from a Sanity array-item key, not copy, a slug, or a translated string
                    analyticsId={card.thumbnailMetadata.analyticsId}
                    className="border-current/10 data-selected:z-1 data-selected:text-current data-selected:bg-chalk text-current/50 hover:text-current/75 hover:z-1 text-label-lg -ml-px flex w-full cursor-pointer items-center border border-b-0 p-2 py-3 text-left first:ml-0 md:p-4 md:px-6 lg:px-10"
                    progress={({ ref, isPlaying, isSelected }) => (
                      <div
                        ref={ref}
                        className={cn(
                          accentClassName,
                          "absolute left-0 top-0 h-0.5 w-full origin-left",
                        )}
                        style={{
                          animation: "carouselAutoplay linear",
                          opacity: isPlaying && isSelected ? 1 : 0,
                        }}
                      />
                    )}
                  >
                    {card.company.name}
                  </Carousel.Thumbnail>
                ))}
              </Carousel.Thumbnails>

              <div className="bg-clay relative overflow-hidden">
                <div className="grid" aria-hidden>
                  {cards.map((card, index) => (
                    <div
                      key={card.company.name}
                      className="[grid-area:1/1]"
                      style={index > 0 ? { visibility: "hidden" } : undefined}
                    >
                      <Testimonial
                        dotDecorators={false}
                        verticalAlignment="center"
                        className="bg-chalk opacity-0"
                        {...card}
                      />
                    </div>
                  ))}
                </div>

                <Carousel.Viewport className="bg-chalk absolute inset-0">
                  <Carousel.Container className="h-full">
                    {cards.map((card) => (
                      <Carousel.Slide
                        key={card.company.name}
                        className="h-full basis-full"
                      >
                        <Testimonial
                          verticalAlignment="center"
                          dotDecoratorClassName="text-clay"
                          className="bg-chalk border-current/10 h-full w-full border"
                          {...card}
                          // eslint-disable-next-line analytics/id-must-be-static -- each card in the cards prop carries its own cardMetadata.analyticsId, built by the caller from a Sanity array-item key, not copy, a slug, or a translated string
                          analyticsId={card.cardMetadata.analyticsId}
                        />
                      </Carousel.Slide>
                    ))}
                  </Carousel.Container>
                </Carousel.Viewport>
              </div>
            </Carousel.Root>
          )}
        </div>
      </div>
    </Section>
  );
}
